import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { funcionarioService, Funcionario } from '../../services/funcionario';

const FuncionarioVisualizar: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Buscar funcionário por ID
  const { data: funcionario, isLoading, error } = useQuery({
    queryKey: ['funcionario', id],
    queryFn: () => funcionarioService.getById(Number(id)),
    enabled: !!id,
  });

  const handleEdit = () => {
    navigate(`/funcionarios/editar/${id}`);
  };

  const handleBack = () => {
    navigate('/funcionarios');
  };

  const formatCPF = (cpf: string) => {
    if (!cpf || cpf.length !== 11) return cpf;
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatTelefone = (telefone: string) => {
    if (!telefone || telefone.length !== 10) return telefone;
    return telefone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (error || !funcionario) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Erro ao carregar os dados do funcionário.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 'bold',
            fontFamily: 'Arial, sans-serif',
            fontSize: '2.125rem',
            lineHeight: 1.235
          }}
        >
          Visualizar Funcionário
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
          >
            Editar
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Voltar
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
        {/* Dados Pessoais */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Dados Pessoais
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Código
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  #{funcionario.id}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Nome
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {funcionario.nome}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  CPF
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium', fontFamily: 'monospace' }}>
                  {formatCPF(funcionario.cpf)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  RG
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {funcionario.rg}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  Data de Nascimento
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {new Date(funcionario.dataNasc).toLocaleDateString('pt-BR')}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={funcionario.ativo === 1 ? 'Ativo' : 'Inativo'}
                  color={funcionario.ativo === 1 ? 'success' : 'default'}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Dados de Contato */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              <EmailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Dados de Contato
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  E-mail
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {funcionario.email}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  Telefone 1
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {formatTelefone(funcionario.telefone1)}
                </Typography>
              </Box>

              {funcionario.telefone2 && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Telefone 2
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {formatTelefone(funcionario.telefone2)}
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Dados Profissionais */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              <WorkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Dados Profissionais
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Cargo
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {funcionario.nomeCargo || '-'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  Data de Contratação
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {new Date(funcionario.dataContratacao).toLocaleDateString('pt-BR')}
                </Typography>
              </Box>

              {funcionario.dataRecisao && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Data de Recisão
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {new Date(funcionario.dataRecisao).toLocaleDateString('pt-BR')}
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Endereço */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Endereço
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  CEP
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {funcionario.cep}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  Logradouro
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {funcionario.logradouro}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  Número
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {funcionario.numero}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  Bairro
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {funcionario.bairro}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  Cidade
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {funcionario.cidade}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  Estado
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {funcionario.estado}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default FuncionarioVisualizar;
